// 0.62.1.17 — User-Preferences-Service (FE-only Pilot, localStorage).
//
// Generischer Service fuer User-eigene UI-Praeferenzen (Profilbild,
// Theme, ...). Erstes Beispiel: Profilbild im Header-Avatar.
//
// Pilot-Stub: localStorage. Stufe 2 (DB-Persistenz pro User auf
// `persons.profile_image_*`-Spalten oder eigene `user_preferences`-
// Tabelle) ist Codex-pflichtig wegen PII-Surface.

import { Injectable, signal, computed } from '@angular/core';

const LS_KEY_PROFILE_IMAGE = 'fap.user-prefs.profile-image';
const MAX_PROFILE_IMAGE_BYTES = 100 * 1024; // 100 KB — Avatar ist klein

@Injectable({ providedIn: 'root' })
export class UserPreferencesService {
  /** Profilbild als URL (z.B. CDN) ODER Base64-Data-URL. null = Initials-Fallback. */
  readonly profileImage = signal<string | null>(null);

  readonly maxProfileImageBytes = MAX_PROFILE_IMAGE_BYTES;

  constructor() {
    try {
      this.profileImage.set(localStorage.getItem(LS_KEY_PROFILE_IMAGE) || null);
    } catch {
      // Browser ohne localStorage → kein Profilbild, kein Crash.
    }
  }

  /** Speichert URL oder Base64. Bei zu grossem Base64 false zurueck
   *  (quota-defensives localStorage-Pattern). */
  setProfileImage(value: string | null): boolean {
    if (!value) {
      try { localStorage.removeItem(LS_KEY_PROFILE_IMAGE); } catch { /* noop */ }
      this.profileImage.set(null);
      return true;
    }
    try {
      localStorage.setItem(LS_KEY_PROFILE_IMAGE, value);
    } catch {
      // Quota exceeded — nicht setzen, Signal unveraendert.
      return false;
    }
    this.profileImage.set(value);
    return true;
  }

  reset(): void {
    this.setProfileImage(null);
  }

  /** Schema-Whitelist analog BrandingService.isValidHttpUrl + Base64-Check. */
  isValidProfileImageSrc(src: string | null | undefined): boolean {
    if (!src) return false;
    const trimmed = src.trim();
    if (/^https?:\/\/[^\s]+$/i.test(trimmed)) return true;
    if (/^data:image\/(png|jpeg|jpg|webp|gif|svg\+xml);base64,/.test(trimmed)) return true;
    return false;
  }

  /** Computed: Effektive Source fuer den Avatar (validiert). */
  readonly effectiveProfileImage = computed<string | null>(() => {
    const v = this.profileImage();
    return this.isValidProfileImageSrc(v) ? v : null;
  });
}
